import React from 'react';
import { Truck, Zap, Globe, Package, ArrowRight, MessageCircle, Mail, Phone } from 'lucide-react';
import Header from './components/Header21';
import Footer from './components/Footer21';

const ShippingAndExchange21 = () => {
    const shippingOptions = [
        {
            icon: Truck,
            title: 'Standard Shipping',
            time: '5-7 Business Days',
            cost: 'Free on orders over \u20B92,999',
            description: 'Reliable delivery across India via our trusted logistics partners. Orders under \u20B92,999 are charged a flat \u20B9149 shipping fee.'
        },
        {
            icon: Zap,
            title: 'Express Shipping',
            time: '2-3 Business Days',
            cost: '\u20B9499',
            description: 'Priority dispatch and expedited delivery for when you need your order sooner. Available for most metro cities and tier-1 locations.'
        },
        {
            icon: Globe,
            title: 'International Shipping',
            time: '7-14 Business Days',
            cost: 'Varies by country',
            description: 'We ship to select countries worldwide. Shipping costs are calculated at checkout based on destination and package weight. Customs duties may apply.'
        }
    ];

    const exchangeSteps = [
        {
            step: 1,
            title: 'Request',
            description: 'Initiate an exchange request within 7 days of delivery through your account dashboard or by contacting our support team with your order number.'
        },
        {
            step: 2,
            title: 'Approval',
            description: 'Our team will review your request within 24-48 hours and send you a confirmation email with return shipping instructions and a prepaid label.'
        },
        {
            step: 3,
            title: 'Ship Back',
            description: 'Pack the item securely in its original packaging with all tags intact. Drop it off at the nearest courier pickup point or schedule a reverse pickup.'
        },
        {
            step: 4,
            title: 'Receive New',
            description: 'Once we receive and inspect the returned item, your replacement will be dispatched within 2-3 business days. You will receive tracking details via email and SMS.'
        }
    ];

    const importantNotes = [
        'Exchange requests must be raised within 7 days of delivery.',
        'Items must be unused, unwashed, and in their original condition with all tags and labels attached.',
        'Original packaging must be intact and included with the return.',
        'Custom-made, bespoke, or made-to-order garments are not eligible for exchange.',
        'Intimate wear, accessories, and items marked as "Final Sale" cannot be exchanged.',
        'Exchanges are subject to availability of the requested size or colour.',
        'If the replacement item is priced higher, the difference must be paid before dispatch.',
        'For COD orders, exchange shipping is free for the first exchange. Subsequent exchanges may incur shipping charges.',
        'Damaged or defective items will be replaced at no additional cost \u2014 please share photographs within 48 hours of delivery.'
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:wght@300;400;500;600;700&display=swap');
            `}</style>
            <div style={{ fontFamily: "'DM Sans', -apple-system, sans-serif" }} className="min-h-screen bg-[#0A0A0A]">
                <Header />

                <main>
                    {/* Hero Section */}
                    <section className="py-20 lg:py-28 px-4 lg:px-12 border-b border-[#2A2A2A]">
                        <div className="max-w-3xl mx-auto text-center">
                            <div className="w-16 h-16 border border-[#C9A96E]/30 rounded-full flex items-center justify-center mx-auto mb-8">
                                <Package className="w-7 h-7 text-[#C9A96E]" />
                            </div>
                            <h1
                                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                className="text-4xl lg:text-6xl font-light text-[#C9A96E] mb-6 tracking-wide"
                            >
                                Shipping & Exchange
                            </h1>
                            <p className="text-[#8A8A7A] text-lg max-w-2xl mx-auto leading-relaxed">
                                Everything you need to know about our shipping options, delivery timelines, and exchange policy.
                            </p>
                        </div>
                    </section>

                    {/* Shipping Options Cards */}
                    <section className="py-16 lg:py-24 px-4 lg:px-12 border-b border-[#2A2A2A]">
                        <div className="max-w-6xl mx-auto">
                            <div className="text-center mb-16">
                                <p className="text-[#C9A96E] text-sm tracking-[0.3em] uppercase mb-4">Delivery Options</p>
                                <h2
                                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                    className="text-3xl lg:text-4xl font-light text-[#F5F0EB] mb-4"
                                >
                                    Shipping Methods
                                </h2>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                                {shippingOptions.map((option, index) => {
                                    const Icon = option.icon;
                                    return (
                                        <div
                                            key={index}
                                            className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-8 hover:border-[#C9A96E]/50 transition-all duration-500 group text-center"
                                        >
                                            <div className="w-16 h-16 border border-[#C9A96E]/30 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#C9A96E]/10 transition-colors duration-500">
                                                <Icon className="w-7 h-7 text-[#C9A96E]" />
                                            </div>
                                            <h3
                                                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                                className="text-xl lg:text-2xl font-medium text-[#F5F0EB] mb-3"
                                            >
                                                {option.title}
                                            </h3>
                                            <p className="text-[#C9A96E] text-sm font-medium tracking-wide mb-1">
                                                {option.time}
                                            </p>
                                            <p className="text-[#F5F0EB] font-semibold text-lg mb-4">
                                                {option.cost}
                                            </p>
                                            <p className="text-[#8A8A7A] text-sm leading-relaxed">
                                                {option.description}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </section>

                    {/* Exchange Process */}
                    <section className="py-16 lg:py-24 px-4 lg:px-12 border-b border-[#2A2A2A]">
                        <div className="max-w-5xl mx-auto">
                            <div className="text-center mb-16">
                                <p className="text-[#C9A96E] text-sm tracking-[0.3em] uppercase mb-4">Simple Process</p>
                                <h2
                                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                    className="text-3xl lg:text-4xl font-light text-[#F5F0EB] mb-4"
                                >
                                    How Exchanges Work
                                </h2>
                                <p className="text-[#8A8A7A] text-lg max-w-2xl mx-auto">
                                    Our streamlined exchange process ensures a hassle-free experience in four simple steps.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                                {exchangeSteps.map((item, index) => (
                                    <div key={index} className="relative text-center">
                                        {/* Gold numbered circle */}
                                        <div className="w-14 h-14 bg-[#C9A96E] rounded-full flex items-center justify-center mx-auto mb-6">
                                            <span
                                                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                                className="text-[#0A0A0A] text-xl font-semibold"
                                            >
                                                {item.step}
                                            </span>
                                        </div>

                                        {/* Arrow connector (hidden on last item and mobile) */}
                                        {index < exchangeSteps.length - 1 && (
                                            <div className="hidden lg:block absolute top-7 left-[calc(50%+40px)] w-[calc(100%-80px)]">
                                                <ArrowRight className="w-5 h-5 text-[#C9A96E]/40 mx-auto" />
                                            </div>
                                        )}

                                        <h3
                                            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                            className="text-xl font-medium text-[#F5F0EB] mb-3"
                                        >
                                            {item.title}
                                        </h3>
                                        <p className="text-[#8A8A7A] text-sm leading-relaxed">
                                            {item.description}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Important Notes */}
                    <section className="py-16 lg:py-24 px-4 lg:px-12 border-b border-[#2A2A2A]">
                        <div className="max-w-3xl mx-auto">
                            <div className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-8 lg:p-10 border-l-4 border-l-[#C9A96E]">
                                <h2
                                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                    className="text-2xl lg:text-3xl font-light text-[#F5F0EB] mb-8"
                                >
                                    Important Notes
                                </h2>
                                <ul className="space-y-4">
                                    {importantNotes.map((note, index) => (
                                        <li key={index} className="flex items-start gap-3">
                                            <span className="w-1.5 h-1.5 bg-[#C9A96E] rounded-full mt-2.5 flex-shrink-0" />
                                            <p className="text-[#F5F0EB]/80 text-base leading-[1.7]">
                                                {note}
                                            </p>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* Need Help Section */}
                    <section className="py-16 lg:py-24 px-4 lg:px-12">
                        <div className="max-w-3xl mx-auto text-center">
                            <p className="text-[#C9A96E] text-sm tracking-[0.3em] uppercase mb-4">We're Here For You</p>
                            <h2
                                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                className="text-3xl lg:text-4xl font-light text-[#F5F0EB] mb-6"
                            >
                                Need Help?
                            </h2>
                            <p className="text-[#8A8A7A] text-lg mb-10 max-w-xl mx-auto leading-relaxed">
                                Our customer support team is available to assist you with any shipping or exchange queries.
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                                <a
                                    href="https://wa.me/919876543210"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex flex-col items-center gap-3 p-6 bg-[#141414] border border-[#2A2A2A] rounded-lg hover:border-[#C9A96E]/50 transition-all duration-300"
                                >
                                    <MessageCircle className="w-6 h-6 text-[#C9A96E]" />
                                    <span className="text-[#C9A96E] font-medium text-sm">WhatsApp Us</span>
                                    <span className="text-[#8A8A7A] text-xs">+91 98765 43210</span>
                                </a>
                                <a
                                    href="mailto:support@yourbrand.com"
                                    className="flex flex-col items-center gap-3 p-6 bg-[#141414] border border-[#2A2A2A] rounded-lg hover:border-[#C9A96E]/50 transition-all duration-300"
                                >
                                    <Mail className="w-6 h-6 text-[#C9A96E]" />
                                    <span className="text-[#C9A96E] font-medium text-sm">Email Us</span>
                                    <span className="text-[#8A8A7A] text-xs">support@yourbrand.com</span>
                                </a>
                                <a
                                    href="tel:+911234567890"
                                    className="flex flex-col items-center gap-3 p-6 bg-[#141414] border border-[#2A2A2A] rounded-lg hover:border-[#C9A96E]/50 transition-all duration-300"
                                >
                                    <Phone className="w-6 h-6 text-[#C9A96E]" />
                                    <span className="text-[#C9A96E] font-medium text-sm">Call Us</span>
                                    <span className="text-[#8A8A7A] text-xs">+91 12345 67890</span>
                                </a>
                            </div>
                        </div>
                    </section>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default ShippingAndExchange21;
